import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Copy, Download, FileArchive, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import usePageSEO from '../hooks/usePageSEO';
import { downloadFile, getDownload } from '../services/api';

const formatSize = (bytes = 0) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / (1024 ** index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const saveBlob = (response, fallbackName) => {
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || fallbackName || 'download';
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const Downloads = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passwords, setPasswords] = useState({});
  const [downloading, setDownloading] = useState('');
  const [copied, setCopied] = useState('');
  const lang = i18n.language?.substring(0, 2) || 'en';

  usePageSEO({
    title: t('downloads.title'),
    description: t('downloads.description'),
    noIndex: true,
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        if (!slug) {
          setItems([]);
          return;
        }
        const data = await getDownload(slug);
        setItems([data]);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const localized = (value) => {
    if (!value || typeof value !== 'object') return '';
    return value[lang] || value.en || '';
  };

  const handleDownload = async (item) => {
    setDownloading(item.slug);
    try {
      const response = await downloadFile(item.slug, passwords[item.slug] || '');
      saveBlob(response, item.original_filename);
    } catch {
      toast({ title: t('auth.error'), description: t('downloads.invalidPassword'), variant: 'destructive' });
    } finally {
      setDownloading('');
    }
  };

  const copyLink = (item) => {
    const url = `${window.location.origin}/downloads/${item.slug}`;
    navigator.clipboard.writeText(url);
    setCopied(item.slug);
    window.setTimeout(() => setCopied(''), 1600);
    toast({ title: t('downloads.linkCopied'), description: url });
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50 dark:bg-[#0f0f0f]" data-testid="downloads-page">
      <div className="bg-gradient-to-br from-red-600 to-red-700 text-white py-16 mb-12">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t('downloads.title')}</h1>
          <p className="text-xl text-red-100 mx-auto">{t('downloads.description')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {loading ? (
          <div className="min-h-[55vh] flex flex-col items-center justify-center text-center">
            <div className="h-14 w-14 rounded-full bg-white dark:bg-[#171717] shadow-sm flex items-center justify-center mb-4">
              <Loader2 className="h-7 w-7 animate-spin text-red-600" />
            </div>
            <p className="text-gray-500">{t('downloads.loading')}</p>
          </div>
        ) : items.length === 0 ? (
          <Card className="max-w-xl mx-auto mt-10 border-gray-200 dark:border-gray-800">
            <CardContent className="py-16 text-center">
              <FileArchive className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {slug ? t('downloads.notFound') : t('downloads.empty')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="w-full">
            {items.map((item) => (
              <div key={item.id} className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-[#171717]">
                <div className="bg-gradient-to-br from-red-600 to-red-700 px-6 py-7 text-white sm:px-8 dark:bg-[#171717] dark:bg-none">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                    <div className="h-14 w-14 rounded-lg bg-white text-red-600 flex items-center justify-center shrink-0 dark:bg-red-600 dark:text-white">
                      <FileArchive className="h-7 w-7" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {item.category && (
                          <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/80">{item.category}</span>
                        )}
                        {item.requires_password && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-600/25 px-2.5 py-1 text-xs font-semibold text-red-100">
                            <Lock className="h-3 w-3" />{t('downloads.protected')}
                          </span>
                        )}
                      </div>
                      <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{localized(item.title)}</h1>
                      {localized(item.description) && (
                        <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/70">{localized(item.description)}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-6 sm:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 md:items-end">
                    <div className="space-y-5">
                      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-[#111111]">
                        <div className="text-xs font-semibold uppercase text-gray-500 mb-2">{t('downloads.fileSize')}</div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                          <span className="font-semibold text-gray-900 dark:text-white break-all">{item.original_filename}</span>
                          <span className="text-sm text-gray-500">{formatSize(item.file_size)}</span>
                        </div>
                      </div>

                      {item.requires_password ? (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/60 dark:bg-red-950/25">
                          <div className="flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-100 mb-3">
                            <ShieldCheck className="h-4 w-4" />
                            {t('downloads.protected')}
                          </div>
                          <Input
                            type="password"
                            value={passwords[item.slug] || ''}
                            onChange={(e) => setPasswords({ ...passwords, [item.slug]: e.target.value })}
                            placeholder={t('downloads.enterPassword')}
                          />
                        </div>
                      ) : null}
                    </div>

                    <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-48">
                      <Button className="bg-red-600 hover:bg-red-700 text-white h-11" onClick={() => handleDownload(item)} disabled={downloading === item.slug}>
                        {downloading === item.slug ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                        {item.requires_password ? t('downloads.unlockDownload') : t('downloads.download')}
                      </Button>
                      <Button variant="outline" className="h-11" onClick={() => copyLink(item)}>
                        {copied === item.slug ? <CheckCircle2 className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                        {t('downloads.copyLink')}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Downloads;
